import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import CourseFetcher from './CourseFetcher';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLink, faMusic } from '@fortawesome/free-solid-svg-icons';
import { forceSetVideo, toggleJoinModal } from 'redux/actions';
import { getEnvVar } from 'utils/get-env-var';
import { NothingSelected } from '.';

const ChapterList = styled.div``;
const Chapter = styled.div`
    margin: 15px 0;
`;
const ChapterName = styled.h3`
    margin: 0 0 5px 0;
    font-size: 17px;
`;
const LessonBoxes = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    gap: 7px;
`;

const LessonLink = styled.a`
    background-color: #fff;
    border-radius: 50%;
    width: 25px;
    height: 25px;
    color: #000;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    ${props => {
        if (props.$name) {
            return `
                &::after {
                    content: '${props.$name}';
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    z-index: 999;
                    transform: translateX(-50%);
                    background-color: #fff;
                    padding: 5px 7px;
                    border-radius: 3px;
                    display: none;

                    width: max-content;
                    max-width: 200px;

                    margin-top: 5px;
                    text-align: center;
                }

                &:hover::after {
                    display: inline-block;
                }
            `;
        }
    }}
`;

const AudioLink = styled(Link)`
    background-color: #fff;
    border-radius: 50%;
    width: 25px;
    height: 25px;
    color: #000;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    font-size: 12px;

    ${props => {
        if (props.$name) {
            return `
                &::after {
                    content: '${props.$name}';
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    z-index: 999;
                    transform: translateX(-50%);
                    background-color: #fff;
                    padding: 5px 7px;
                    border-radius: 3px;
                    display: none;

                    width: max-content;
                    max-width: 200px;

                    margin-top: 5px;
                    text-align: center;
                }

                &:hover::after {
                    display: inline-block;
                }
            `;
        }
    }}
`;

const ProgressPost = styled.div`
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 50%;
    width: 25px;
    height: 25px;
    border-radius: 3px;
    margin: 5px;
    position: relative;

    &::after {
        content: 'Progress post';
        position: absolute;
        top: 100%;
        left: 50%;
        z-index: 999;
        transform: translateX(-50%);
        background-color: #fff;
        padding: 5px 7px;
        border-radius: 3px;
        display: none;
        color: #000;

        width: max-content;
        max-width: 200px;

        margin-top: 5px;
        text-align: center;
        
    }

    &:hover::after {
        display: inline-block;
    }
`;

export const CompleteBox = styled.div`
    margin: 5px;
    width: 25px;
    height: 25px;
    border-radius: 3px;
    background-color: #fff;
    position: relative;

    flex-shrink: 0;
    color: #000;

    .checkmark {
        display: none;

        ${props => {
            if (props.$complete) {
                return `
                    display: block;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 16px;
                `;
            }
        }}
    }
    
    ${props => {
        if (props.$name) {
            return `
                &::after {
                    content: '${props.$name}';
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    z-index: 999;
                    transform: translateX(-50%);
                    background-color: #fff;
                    padding: 5px 7px;
                    border-radius: 3px;
                    display: none;

                    width: max-content;
                    max-width: 200px;

                    margin-top: 5px;
                    text-align: center;
                }

                &:hover::after {
                    display: inline-block;
                }
            `;
        }
    }}
`;

const LessonList = ({ activeSeries, activeCourse }) => {
    const series = useSelector(state => state.courseList.series.series);
    const courseDetails = useSelector(state => state.currentCourse.courseDetails.course);
    const isSubscriber = useSelector(state => state.user.userData.isSubscriber);

    const dispatch = useDispatch();

    const courseSlug = useMemo(() => {
        if (!series) return;
        const courses = series[activeSeries]?.courses || [];
        const course = courses[activeCourse];
        return course?.slug;
    }, [series, activeSeries, activeCourse]);

    const loadLesson = (lesson) => {
        dispatch(forceSetVideo(lesson.id));
    };

    if (!courseSlug) {
        return <NothingSelected>Select a course</NothingSelected>;
    }

    return (
        <CourseFetcher courseSlug={courseSlug}>
            <ChapterList>
                {(courseDetails?.contents || []).map((chapter, index) => (
                    <Chapter key={index} style={{ cursor: 'pointer' }}>
                        <ChapterName>{chapter.name}</ChapterName>
                        <LessonBoxes>
                            {chapter.videos.map((lesson, lessonIndex) => {
                                if (lesson.type === 'link') {
                                    return (
                                        <LessonLink
                                            href={isSubscriber ? lesson.link : '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            key={lessonIndex}
                                            $name={lesson.name}
                                        >
                                            <FontAwesomeIcon icon={faLink} />
                                        </LessonLink>
                                    );
                                }

                                if (lesson.type === 'progress') {
                                    return <ProgressPost key={lessonIndex} />;
                                }

                                if (lesson.type === 'audio') {
                                    return (
                                        <AudioLink
                                            to={`${getEnvVar('ROOT_ROUTE')}/course/${courseDetails.slug}`}
                                            onClick={() => loadLesson(lesson)} 
                                            key={lessonIndex}
                                            $name={lesson.name}
                                        >
                                            <FontAwesomeIcon icon={faMusic} />
                                        </AudioLink>
                                    );
                                }

                                if (!isSubscriber) {
                                    return (
                                        <CompleteBox key={lessonIndex} $name={lesson.name} onClick={() => dispatch(toggleJoinModal())}>
                                            <FontAwesomeIcon icon={faCheck} className="checkmark" />
                                        </CompleteBox>
                                    );
                                }

                                return (
                                    <Link  
                                        to={`${getEnvVar('ROOT_ROUTE')}/course/${courseDetails.slug}`} 
                                        onClick={() => loadLesson(lesson)} 
                                        key={lessonIndex}
                                    >
                                        <CompleteBox $name={lesson.name} $complete={lesson.complete}>
                                            <FontAwesomeIcon icon={faCheck} className="checkmark" />
                                        </CompleteBox>
                                    </Link>
                                );
                            })}
                        </LessonBoxes>
                    </Chapter>
                ))}
            </ChapterList>
        </CourseFetcher>

    );
};

export default LessonList;
